import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

/**
 * Design defaults translated from the builder component's `settings.ts`.
 *
 * The builder styles against Wix Studio theme tokens (`--wst-heading-2-color`,
 * `--wst-paragraph-2-font`, …), which don't exist in OOI. Each one maps to its
 * site-palette equivalent here:
 *
 *   --wst-heading-2-*        → Page-title / color-5   (headings)
 *   --wst-paragraph-2-*      → Body-M / color-5       (body text)
 *   --wst-paragraph-3-*      → Body-S                 (small text)
 *   --wst-shade-3-color      → color-4                (subtle text)
 *   --wst-links-and-actions  → color-8                (actions)
 *   --wst-system-line-1      → color-2                (hairlines)
 *   --wst-primary-background → color-1                (surfaces)
 */
export type IStylesParams = {
  titleFont: StyleParamType.Font;
  titleColor: StyleParamType.Color;
  listPaddingTop: StyleParamType.Number;
  listPaddingRight: StyleParamType.Number;
  listPaddingBottom: StyleParamType.Number;
  listPaddingLeft: StyleParamType.Number;
  gridGap: StyleParamType.Number;
  maxColumns: StyleParamType.Number;
  cardBackgroundColor: StyleParamType.Color;
  cardBorderColor: StyleParamType.Color;
  cardBorderWidth: StyleParamType.Number;
  cardBorderRadius: StyleParamType.Number;
  imageBorderRadius: StyleParamType.Number;
  imagePlaceholderColor: StyleParamType.Color;
  badgeBackgroundColor: StyleParamType.Color;
  badgeBorderColor: StyleParamType.Color;
  badgeTextColor: StyleParamType.Color;
  badgeFont: StyleParamType.Font;
  serviceNameFont: StyleParamType.Font;
  serviceNameColor: StyleParamType.Color;
  priceFont: StyleParamType.Font;
  priceColor: StyleParamType.Color;
  unitPriceColor: StyleParamType.Color;
  durationRangeFont: StyleParamType.Font;
  durationRangeColor: StyleParamType.Color;
  taglineFont: StyleParamType.Font;
  taglineColor: StyleParamType.Color;
  bookButtonFont: StyleParamType.Font;
  bookButtonColor: StyleParamType.Color;
  bookButtonIconColor: StyleParamType.Color;
  bookButtonIconSize: StyleParamType.Number;
  bookButtonIconRotation: StyleParamType.Number;
  bookButtonContentGap: StyleParamType.Number;
  emptyStateFont: StyleParamType.Font;
  emptyStateColor: StyleParamType.Color;
  chipBorderColor: StyleParamType.Color;
  chipTextColor: StyleParamType.Color;
  chipFont: StyleParamType.Font;
  modalTitleFont: StyleParamType.Font;
  modalTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  applyBackgroundColor: StyleParamType.Color;
  applyTextColor: StyleParamType.Color;
  searchBorderColor: StyleParamType.Color;
  searchTextColor: StyleParamType.Color;
  searchPlaceholderColor: StyleParamType.Color;
  searchFont: StyleParamType.Font;
  searchButtonBackgroundColor: StyleParamType.Color;
  searchButtonIconColor: StyleParamType.Color;
  searchInputIconColor: StyleParamType.Color;
  calendarBackgroundColor: StyleParamType.Color;
  calendarBorderColor: StyleParamType.Color;
  optionHoverBackgroundColor: StyleParamType.Color;
  dividerColor: StyleParamType.Color;
  disabledTextColor: StyleParamType.Color;
  loadMoreButtonFont: StyleParamType.Font;
  loadMoreButtonColor: StyleParamType.Color;
  loadMoreButtonBorderColor: StyleParamType.Color;
};

export default createStylesParams<IStylesParams>({
  titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 28 }),
  },
  titleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  listPaddingTop: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  listPaddingRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  listPaddingBottom: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  listPaddingLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  gridGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  maxColumns: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  cardBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  cardBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  // The builder ships a borderless card (`0px solid`) with the radius still set,
  // so the border only appears once a user opts into a width.
  cardBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  cardBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  imageBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  // Light neutral block shown where a rental has no image.
  imagePlaceholderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  badgeBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  badgeBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  badgeTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  badgeFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 12,
      style: { bold: false, italic: false, underline: false },
    }),
  },
  serviceNameFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  serviceNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  priceFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  priceColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  unitPriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  durationRangeFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S', { size: 14 }),
  },
  durationRangeColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  taglineFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  taglineColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  bookButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  // The CTA reads as a link, not body text — color-8 is the links slot.
  bookButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonIconSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  bookButtonIconRotation: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  bookButtonContentGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  emptyStateFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  emptyStateColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  // Filter chips and the filters button share one outline + label treatment.
  chipBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  chipTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  chipFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  modalTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  modalTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  // Apply is the one filled control in the modal.
  applyBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  applyTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  // Search pill: outlined fields with a filled submit at the trailing end.
  searchBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  searchTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  // Unselected location/date read as placeholder text until a value is picked.
  searchPlaceholderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  searchFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  searchButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  searchButtonIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  searchInputIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  calendarBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  calendarBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  // Tint behind a hovered or selected dropdown row.
  optionHoverBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  // Hairlines between modal sections, header and footer.
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  // Disabled control text, e.g. "Clear all" with nothing selected.
  disabledTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  // Outlined "Load more" button: the builder's `.loadMore` is border + text in
  // one dark shade (`#111827`), independent of the filter chips it used to
  // borrow from.
  loadMoreButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
      style: { bold: false, italic: false, underline: false },
    }),
  },
  loadMoreButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  loadMoreButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
});
